import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHasAccess } from '@broadleaf/admin-components/dist/authentication/hooks';
import Button from '@broadleaf/admin-components/dist/common/elements/Button';
import SimpleModal from '@broadleaf/admin-components/dist/common/components/SimpleModal';
import { ToastContainerContext } from '@broadleaf/admin-components/dist/common/index';
import { getEndpointByType } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import { IViewCommonAction } from '@broadleaf/admin-components/dist/types/view';
import { ContextParameters } from '@broadleaf/admin-components/dist/tracking/utils/ContextRequest';
import { queueFetch } from '@broadleaf/admin-components/dist/view/named-components/view-types/EntityView/state/actions/fetchActions';
import useTracking from '@src/components/view/navigation/hooks/useTracking';

const PricingRequestWorksheetAction: React.FC<IViewCommonAction> = ({
  actionDefinition,
  formik,
  dispatch
}) => {
  const tracking = useTracking();
  const toast = useContext(ToastContainerContext);
  const hasAccess = useHasAccess(
    actionDefinition.scope,
    actionDefinition.operationType
  );
  const cost = actionDefinition.id === 'generate-cost-worksheet';
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [option, setOption] = useState('FULL_WORKSHEET');
  const [score, setScore] = useState(3);
  const [error, setError] = useState('');
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  const confidence =
    option === 'HIGH_CONFIDENCE_WORKSHEET' ||
    option === 'LOW_CONFIDENCE_WORKSHEET';
  const valid =
    option !== 'NONE' &&
    option !== 'AUTO_APPROVE' &&
    (!confidence || (Number.isInteger(score) && score >= 1 && score <= 10));

  const show = () => {
    const preferences = formik.values.preferences || {};
    setOption(
      (cost ? preferences.costWorksheet : preferences.pricingWorksheetReview) ||
        'FULL_WORKSHEET'
    );
    setScore(preferences.minimumConfidenceScore || 3);
    setError('');
    setOpen(true);
  };

  const generate = async () => {
    setBusy(true);
    setError('');
    const context = {
      [ContextParameters.TRACKING]: tracking,
      id: formik.values.id
    };
    try {
      await request(
        {
          ...getEndpointByType(actionDefinition, 'GENERATE'),
          data: {
            version: formik.values.version,
            [cost ? 'costWorksheet' : 'pricingWorksheetReview']: option,
            ...(confidence ? { minimumConfidenceScore: score } : {})
          }
        },
        context
      );
      if (!mounted.current) return;
      toast.addToast(
        `${cost ? 'Cost' : 'Pricing'} worksheet generation started.`,
        { type: 'success', autoClose: 5000 }
      );
      setOpen(false);
    } catch (e) {
      if (!mounted.current) return;
      setError(
        e instanceof Error
          ? e.message
          : 'Unable to generate the worksheet. Please try again.'
      );
    } finally {
      if (mounted.current) {
        setBusy(false);
        dispatch(queueFetch());
      }
    }
  };

  if (!hasAccess) return null;
  return (
    <>
      <Button color="primary" type="button" onClick={show} disabled={busy}>
        {actionDefinition.label}
      </Button>
      {open && (
        <SimpleModal
          isOpen
          title={actionDefinition.label}
          closeOnClickOutside={false}
          onClose={() => {
            if (!busy) setOpen(false);
          }}
          footer={
            <>
              <Button
                type="button"
                disabled={busy}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                color="primary"
                disabled={busy || !valid}
                onClick={generate}
              >
                {busy ? 'Starting generation…' : actionDefinition.label}
              </Button>
            </>
          }
        >
          <label className="block mb-4">
            {cost
              ? 'Cost Worksheet generation'
              : 'Pricing Worksheet generation'}
            <select
              className="block w-full mt-2"
              value={option}
              disabled={busy}
              onChange={e => setOption(e.target.value)}
            >
              <option value="FULL_WORKSHEET">
                Generate full {cost ? 'cost' : 'pricing'} worksheet
              </option>
              {cost ? (
                <>
                  <option value="LOW_CONFIDENCE_WORKSHEET">
                    Generate low confidence cost worksheet
                  </option>
                  <option value="MISSING_PRICES_WORKSHEET">
                    Generate missing prices cost worksheet
                  </option>
                  <option value="NONE">Do not generate cost worksheet</option>
                </>
              ) : (
                <>
                  <option value="HIGH_CONFIDENCE_WORKSHEET">
                    Generate high confidence pricing worksheet
                  </option>
                  <option value="AUTO_APPROVE">
                    Auto approve - do not send pricing worksheet
                  </option>
                </>
              )}
            </select>
          </label>
          {confidence && (
            <label className="block mb-4">
              Minimum confidence score
              <input
                className="block mt-2"
                type="number"
                min={1}
                max={10}
                step={1}
                value={score}
                disabled={busy}
                onChange={e => setScore(Number(e.target.value))}
              />
            </label>
          )}
          {!valid && (
            <p>
              Select a worksheet generation option and a confidence score from 1
              to 10, if applicable.
            </p>
          )}
          {error && <p role="alert">{error}</p>}
        </SimpleModal>
      )}
    </>
  );
};
export default PricingRequestWorksheetAction;
